import { Alert, Button, Collapse, Input, InputNumber, Select, Space, Tag, Tooltip, Typography } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import { JsonSchema, enumValues, isPrivate, resolveSchema, unknownOptionPaths } from "../sdkSchema";

type ConfigObject = Record<string, unknown>;

const isEmpty = (value: unknown) =>
  value === undefined ||
  value === "" ||
  (Array.isArray(value) && value.length === 0) ||
  (value !== null && typeof value === "object" && !Array.isArray(value) && Object.keys(value).length === 0);

const setIn = (object: ConfigObject, key: string, value: unknown): ConfigObject => {
  const { [key]: _removed, ...rest } = object;
  return value === undefined ? rest : { ...rest, [key]: value };
};

const deleteIn = (object: ConfigObject, path: string[]): ConfigObject => {
  const [key, ...rest] = path;
  if (rest.length === 0) return setIn(object, key, undefined);
  const nested = object[key];
  return nested && typeof nested === "object" ? { ...object, [key]: deleteIn(nested as ConfigObject, rest) } : object;
};

export const FieldLabel = ({ name, schema }: { name: string; schema: JsonSchema }) => {
  const resolved = resolveSchema(schema);
  const defaultValue = resolved.default;
  return (
    <Space size={4} wrap>
      <Typography.Text code>{name}</Typography.Text>
      {schema.description && (
        <Tooltip title={<span style={{ whiteSpace: "pre-wrap" }}>{schema.description}</span>} overlayStyle={{ maxWidth: 420 }}>
          <InfoCircleOutlined style={{ color: "#8c8c8c" }} />
        </Tooltip>
      )}
      {schema.deprecated !== undefined && schema.deprecated !== false && (
        <Tooltip title={typeof schema.deprecated === "string" ? schema.deprecated : undefined}>
          <Tag color="orange">deprecated</Tag>
        </Tooltip>
      )}
      {isPrivate(schema) && <Tag color="purple">internal</Tag>}
      {defaultValue !== undefined && (
        <Tooltip title="SDK default when this option is not set; not added to your configuration.">
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Default: {JSON.stringify(defaultValue)}
          </Typography.Text>
        </Tooltip>
      )}
    </Space>
  );
};

/** Fallback editor for values the form can't represent directly, e.g. arrays of objects or records. */
const JsonInput = ({ value, onChange }: { value: unknown; onChange: (value: unknown) => void }) => {
  const format = (v: unknown) => (v === undefined ? "" : JSON.stringify(v, null, 2));
  const [text, setText] = useState(format(value));
  const [error, setError] = useState<string>();

  useEffect(() => setText(format(value)), [value]);

  return (
    <>
      <Input.TextArea
        autoSize={{ minRows: 1, maxRows: 8 }}
        placeholder="JSON"
        status={error ? "error" : undefined}
        style={{ fontFamily: "monospace", fontSize: 12 }}
        value={text}
        onChange={(event) => setText(event.target.value)}
        onBlur={() => {
          if (text.trim() === "") {
            setError(undefined);
            onChange(undefined);
            return;
          }
          try {
            onChange(JSON.parse(text));
            setError(undefined);
          } catch {
            setError("Invalid JSON");
          }
        }}
      />
      {error && <Typography.Text type="danger">{error}</Typography.Text>}
    </>
  );
};

interface FieldProps {
  schema: JsonSchema;
  value: unknown;
  onChange: (value: unknown) => void;
  showPrivate: boolean;
}

const FieldInput = ({ schema, value, onChange, showPrivate }: FieldProps) => {
  const resolved = resolveSchema(schema);
  const literals = enumValues(resolved);

  if (resolved.type === "boolean") {
    return (
      <Space.Compact size="small">
        {[true, false].map((option) => (
          <Button
            key={String(option)}
            type={value === option ? "primary" : "default"}
            onClick={() => onChange(value === option ? undefined : option)}
          >
            {String(option)}
          </Button>
        ))}
      </Space.Compact>
    );
  }
  if (literals) {
    return (
      <Select
        allowClear
        style={{ width: "100%" }}
        options={literals.map((literal) => ({ label: String(literal), value: literal as string }))}
        value={value as string | undefined}
        onChange={(selected) => onChange(selected ?? undefined)}
      />
    );
  }
  if (resolved.type === "string") {
    return (
      <Input
        allowClear
        value={(value as string | undefined) ?? ""}
        onChange={(event) => onChange(event.target.value === "" ? undefined : event.target.value)}
      />
    );
  }
  if (resolved.type === "number" || resolved.type === "integer") {
    return (
      <InputNumber
        style={{ width: "100%" }}
        value={value as number | undefined}
        onChange={(number) => onChange(number ?? undefined)}
      />
    );
  }
  if (resolved.type === "array" && resolved.items) {
    const itemLiterals = enumValues(resolved.items);
    const itemType = resolveSchema(resolved.items).type;
    if (itemLiterals || itemType === "string") {
      return (
        <Select
          allowClear
          mode={itemLiterals ? "multiple" : "tags"}
          style={{ width: "100%" }}
          options={itemLiterals?.map((literal) => ({ label: String(literal), value: literal as string }))}
          value={value as string[] | undefined}
          onChange={(selected: string[]) => onChange(selected)}
          onClear={() => onChange(undefined)}
        />
      );
    }
  }
  if (resolved.type === "object" && resolved.properties) {
    return (
      <div style={{ borderLeft: "2px solid #f0f0f0", marginLeft: 6, marginTop: 8, paddingLeft: 12 }}>
        <SchemaFields
          schema={resolved}
          value={(value as ConfigObject | undefined) ?? {}}
          onChange={(nested) => onChange(isEmpty(nested) ? undefined : nested)}
          showPrivate={showPrivate}
        />
      </div>
    );
  }
  return <JsonInput value={value} onChange={onChange} />;
};

interface SchemaFieldsProps {
  schema: JsonSchema;
  value: ConfigObject;
  onChange: (value: ConfigObject) => void;
  showPrivate: boolean;
}

const SchemaFields = ({ schema, value, onChange, showPrivate }: SchemaFieldsProps) => {
  const properties = Object.entries(schema.properties ?? {}).filter(
    ([key, property]) => showPrivate || !isPrivate(property) || value[key] !== undefined
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {properties.map(([key, property]) => {
        return (
          <div key={key} data-option={key}>
            <div style={{ marginBottom: 4 }}>
              <FieldLabel name={key} schema={property} />
            </div>
            <FieldInput
              schema={property}
              value={value[key]}
              onChange={(nextValue) => onChange(setIn(value, key, nextValue))}
              showPrivate={showPrivate}
            />
          </div>
        );
      })}
    </div>
  );
};

export interface SchemaFormSection {
  key: string;
  schema: JsonSchema;
}

interface SchemaFormProps {
  sections: SchemaFormSection[];
  value: Record<string, ConfigObject>;
  onChange: (value: Record<string, ConfigObject>) => void;
  showPrivate: boolean;
}

const SchemaForm = ({ sections, value, onChange, showPrivate }: SchemaFormProps) => {
  const updateSection = (key: string, sectionValue: ConfigObject) => onChange({ ...value, [key]: sectionValue });

  return (
    <Collapse
      defaultActiveKey={[]}
      items={sections.map(({ key, schema }) => {
        const sectionValue = value[key] ?? {};
        const unknownPaths = unknownOptionPaths(sectionValue, schema);
        return {
          key,
          label: (
            <Space>
              {key}
              {unknownPaths.length > 0 && <Tag color="red">{unknownPaths.length} unknown</Tag>}
            </Space>
          ),
          children: (
            <>
              {unknownPaths.length > 0 && (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginBottom: 12 }}
                  message="Options not in the SDK schema"
                  description={
                    <Space direction="vertical" size={4}>
                      {unknownPaths.map((path) => (
                        <Space key={path.join(".")}>
                          <Typography.Text code>{path.join(".")}</Typography.Text>
                          <Button
                            size="small"
                            type="link"
                            onClick={() => updateSection(key, deleteIn(sectionValue, path))}
                          >
                            Remove
                          </Button>
                        </Space>
                      ))}
                    </Space>
                  }
                />
              )}
              <SchemaFields
                schema={schema}
                value={sectionValue}
                onChange={(nextValue) => updateSection(key, nextValue)}
                showPrivate={showPrivate}
              />
            </>
          ),
        };
      })}
    />
  );
};

export default SchemaForm;
